import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Formats an amount with thousands separators while it is typed ("1,250,000.5")
 * and hands the form a plain number (1250000.5), or null when empty. Use it in
 * place of type="number" on amount fields, with ngModel or a form control:
 *
 *   <input type="text" appAmountInput [(ngModel)]="amount" />
 *
 * Only digits and one decimal point are accepted (plus a leading minus with
 * `amountAllowNegative`); the caret is kept next to the same digit as
 * separators are added or removed.
 */
@Directive({
  selector: 'input[appAmountInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmountInput),
      multi: true,
    },
  ],
  host: {
    inputmode: 'decimal',
    autocomplete: 'off',
    dir: 'ltr',
  },
})
export class AmountInput implements ControlValueAccessor {
  /** Maximum digits after the decimal point; 0 makes the field whole numbers only. */
  @Input() amountDecimals = 2;

  /** Accept a leading minus, e.g. for losses in financial statements. */
  @Input() amountAllowNegative = false;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly element: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  writeValue(value: unknown): void {
    this.setView(formatAmount(toAmount(value), this.amountDecimals));
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('input')
  onInput(): void {
    const input = this.element.nativeElement;
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;

    // How many digits / points / minus signs sit before the caret, so it can be restored.
    const significantBeforeCaret = countSignificant(raw.slice(0, caret));

    const typed = normalizeTyped(raw, this.amountDecimals, this.amountAllowNegative);
    const formatted = groupTyped(typed);

    this.setView(formatted);
    this.restoreCaret(formatted, significantBeforeCaret);

    this.onChange(typedToAmount(typed));
  }

  @HostListener('blur')
  onBlur(): void {
    const typed = normalizeTyped(this.element.nativeElement.value, this.amountDecimals, this.amountAllowNegative);
    this.setView(formatAmount(typedToAmount(typed), this.amountDecimals));
    this.onTouched();
  }

  private setView(value: string): void {
    this.renderer.setProperty(this.element.nativeElement, 'value', value);
  }

  private restoreCaret(formatted: string, significantBeforeCaret: number): void {
    const input = this.element.nativeElement;

    if (typeof document !== 'undefined' && document.activeElement !== input) {
      return;
    }

    let position = 0;
    let seen = 0;

    while (position < formatted.length && seen < significantBeforeCaret) {
      if (isSignificant(formatted[position])) {
        seen++;
      }
      position++;
    }

    input.setSelectionRange(position, position);
  }
}

/* ---------- helpers (exported for tests) ---------- */

const GROUPING = /\B(?=(\d{3})+(?!\d))/g;

function isSignificant(character: string): boolean {
  return /[\d.-]/.test(character);
}

function countSignificant(text: string): number {
  let count = 0;
  for (const character of text) {
    if (isSignificant(character)) {
      count++;
    }
  }
  return count;
}

/** Any model value (number, numeric string, possibly with commas) → number | null. */
export function toAmount(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());

  return Number.isFinite(numeric) ? numeric : null;
}

/** Display form of a stored amount: "1,250,000.5"; '' when empty. */
export function formatAmount(value: number | null, decimals = 2): string {
  if (value === null) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(value);
}

/**
 * Keeps what the user is typing, minus anything that is not part of an amount:
 * digits, one decimal point (when decimals are allowed), at most `decimals`
 * fraction digits, and a leading minus when negatives are allowed. Leading
 * zeros are dropped ("007" → "7", "0.5" stays).
 */
export function normalizeTyped(raw: string, decimals = 2, allowNegative = false): string {
  const negative = allowNegative && raw.trim().startsWith('-') ? '-' : '';
  const cleaned = raw.replace(decimals > 0 ? /[^\d.]/g : /\D/g, '');
  const pointIndex = cleaned.indexOf('.');

  let integer = pointIndex === -1 ? cleaned : cleaned.slice(0, pointIndex);
  const fraction = pointIndex === -1 ? null : cleaned.slice(pointIndex + 1).replace(/\./g, '').slice(0, decimals);

  integer = integer.replace(/^0+(?=\d)/, '');

  if (fraction === null) {
    return negative + integer;
  }

  return `${negative}${integer || '0'}.${fraction}`;
}

/** Adds separators to a normalized string, keeping a trailing "." or "5.0" as typed. */
export function groupTyped(typed: string): string {
  const negative = typed.startsWith('-') ? '-' : '';
  const [integer, fraction] = typed.slice(negative.length).split('.');
  const grouped = negative + integer.replace(GROUPING, ',');

  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

export function typedToAmount(typed: string): number | null {
  if (!typed || typed === '.' || typed === '-' || typed === '-.') {
    return null;
  }

  const numeric = Number(typed);
  return Number.isFinite(numeric) ? numeric : null;
}
